// =============================================================================
// SIREPPAR - Adjuntos de respaldo de un informe
// =============================================================================
// Los informes del circuito de reconocimiento se redactan a mano en un modal. Hasta
// ahora el operador solo podía AFIRMAR cosas: si registraba una objeción del Art. 10
// —"se recibió una oposición por correo"— no había dónde poner la prueba.
//
// Este bloque, compartido por los cuatro modales de informe, permite subir esos
// respaldos. Qué hace el backend con ellos (ver utils/adjuntosInforme.ts):
//   - los anexa como HOJAS al final del PDF del informe, que es lo que queda como
//     Documento del expediente;
//   - los PDF, además, los adjunta al correo cuando se notifica al partido.
//
// No se guardan como documento aparte: viven dentro del PDF del informe.
// =============================================================================
import toast from 'react-hot-toast';

/** Tope de adjuntos. Espeja MAX_ADJUNTOS_INFORME del backend. */
export const MAX_ADJUNTOS = 5;
/** Tamaño máximo por archivo. Espeja MAX_MB_ADJUNTO_INFORME del backend. */
export const MAX_MB_ADJUNTO = 20;
/** Adjuntos de observaciones: tope por archivo y por envío (nginx corta el pedido en 50 MB). */
export const MAX_MB_TOTAL_OBSERVACION = 50;
export const superaTotalAdjuntos = (archivos: File[]) =>
  archivos.reduce((suma, a) => suma + a.size, 0) > MAX_MB_TOTAL_OBSERVACION * 1024 * 1024;

const TIPOS_OK = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

/**
 * Agrega archivos a la lista, validando tipo, tamaño y tope. Se exporta aparte del
 * componente para que los modales puedan reusar la validación en su propio estado.
 */
export function agregarAdjuntos(actuales: File[], nuevos: FileList | null, maxMb = MAX_MB_ADJUNTO): File[] {
  if (!nuevos || nuevos.length === 0) return actuales;
  const validos: File[] = [];
  for (const f of Array.from(nuevos)) {
    if (!TIPOS_OK.includes(f.type) && !/\.(pdf|jpe?g|png)$/i.test(f.name)) {
      toast.error(`"${f.name}" no es un PDF ni una imagen (PNG/JPG).`);
      continue;
    }
    if (f.size > maxMb * 1024 * 1024) {
      toast.error(`"${f.name}" supera los ${maxMb} MB.`);
      continue;
    }
    validos.push(f);
  }
  const total = [...actuales, ...validos];
  if (total.length > MAX_ADJUNTOS) {
    toast.error(`Podés adjuntar hasta ${MAX_ADJUNTOS} archivos.`);
    return total.slice(0, MAX_ADJUNTOS);
  }
  return total;
}

export default function AdjuntosInforme({
  archivos,
  onChange,
  // Se destaca cuando el respaldo es lo importante (p. ej. una objeción registrada).
  destacado = false,
  ayuda,
  titulo = 'Adjuntar respaldo (opcional)',
  maxMb = MAX_MB_ADJUNTO,
  // Los informes adjuntan sus PDF al correo; las observaciones solo si entran.
  avisoCorreo = true,
}: {
  archivos: File[];
  onChange: (archivos: File[]) => void;
  destacado?: boolean;
  ayuda?: string;
  titulo?: string;
  maxMb?: number;
  avisoCorreo?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        destacado ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'
      }`}
    >
      <label className="block text-sm font-medium text-gray-700 mb-1">
      {titulo}
        <span className="ml-2 font-normal text-xs text-gray-500">
          PDF o imagen, hasta {MAX_ADJUNTOS} archivos
        </span>
      </label>
      <p className="text-xs text-gray-600 mb-2">
        {ayuda
          ?? 'Captura de pantalla, correo o nota escaneada que respalde lo que se informa.'}
      </p>
      <input
        type="file"
        multiple
        accept="application/pdf,.pdf,image/png,image/jpeg"
        disabled={archivos.length >= MAX_ADJUNTOS}
        onChange={(e) => {
          onChange(agregarAdjuntos(archivos, e.target.files, maxMb));
          // Se limpia el input para poder volver a elegir el mismo archivo.
          e.target.value = '';
        }}
        className="text-sm"
      />
      {archivos.length > 0 && (
        <ul className="mt-2 space-y-1">
          {archivos.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="flex items-center justify-between gap-2 text-xs bg-white border rounded px-2 py-1"
            >
              <span className="truncate">
                Anexo {i + 1} — {f.name}
                {avisoCorreo && f.type === 'application/pdf' && (
                  <span className="ml-1 text-gray-400">(también va como archivo en el correo)</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => onChange(archivos.filter((_, idx) => idx !== i))}
                className="text-red-600 hover:text-red-700 shrink-0"
                title="Quitar"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
